import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ResultadoEscaneoRfid } from '@cmms/shared';
import { ErrorApi, api } from '../servicios/api.js';
import { useSesion } from '../servicios/sesion.js';
import { useToasts } from './notificaciones-globales.js';

/**
 * Lector RFID HID (13.56 MHz). No necesita driver: al acercar una tarjeta «tipea»
 * el UID en hexadecimal seguido de Enter, igual que un teclado.
 *
 * Por eso el componente mantiene un input oculto con el foco. Se vuelve a enfocar
 * solo cuando nada editable lo tiene, así un clic dentro de un formulario no
 * le roba el foco a quien está escribiendo. Un escaneo mientras se escribe en un
 * campo sí entra en ese campo: es el compromiso de un lector de teclado.
 */

/** Cuánto espera la detección antes de decir que no hay lector conectado. */
const TIEMPO_DETECCION_MS = 15000;
/** Promedio entre teclas por debajo del cual la entrada se considera ráfaga de lector. */
const UMBRAL_RAFAGA_MS = 60;

export const EVENTO_VINCULAR_RFID = 'rfid:vincular';

export interface PedidoVincularRfid {
  activoId: string;
  nombre: string;
}

/** La ficha de un activo pide vincular un tag. Primero se detecta el lector. */
export function pedirVincularRfid(pedido: PedidoVincularRfid): void {
  window.dispatchEvent(new CustomEvent<PedidoVincularRfid>(EVENTO_VINCULAR_RFID, { detail: pedido }));
}

function esEditable(elemento: Element | null): boolean {
  if (!(elemento instanceof HTMLElement)) return false;
  return elemento.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(elemento.tagName);
}

function hayDialogoAbierto(): boolean {
  return document.querySelector('[role="dialog"], [aria-modal="true"]') !== null;
}

export function LectorRfid(): JSX.Element | null {
  const { puede } = useSesion();
  const cliente = useQueryClient();
  const { mostrarExito, mostrarError } = useToasts();
  const catcherRef = useRef<HTMLInputElement>(null);
  /** Instante de cada tecla de la lectura en curso: de ahí sale si vino de un lector. */
  const marcasRef = useRef<number[]>([]);
  const [vinculando, setVinculando] = useState<PedidoVincularRfid | null>(null);
  /** UID leído mientras se vincula: el lector ya está confirmado y falta la confirmación. */
  const [detectado, setDetectado] = useState<string | null>(null);

  const puedeLeer = puede('activo:leer');
  const puedeVincular = puede('activo:escribir');

  useEffect(() => {
    const reenfocar = () => {
      if (hayDialogoAbierto() || esEditable(document.activeElement)) return;
      catcherRef.current?.focus({ preventScroll: true });
    };
    document.addEventListener('click', reenfocar);
    reenfocar();
    return () => document.removeEventListener('click', reenfocar);
  }, []);

  useEffect(() => {
    if (!puedeVincular) return;
    const alPedir = (evento: Event) => {
      setDetectado(null);
      setVinculando((evento as CustomEvent<PedidoVincularRfid>).detail);
      catcherRef.current?.focus({ preventScroll: true });
    };
    window.addEventListener(EVENTO_VINCULAR_RFID, alPedir);
    return () => window.removeEventListener(EVENTO_VINCULAR_RFID, alPedir);
  }, [puedeVincular]);

  // Sin ninguna lectura en el tiempo previsto, no hay lector que escuche: se avisa
  // y no se deja un modo de vinculación colgado.
  useEffect(() => {
    if (!vinculando || detectado) return;
    const temporizador = setTimeout(() => {
      setVinculando(null);
      mostrarError('No se detectó ningún lector RFID. Conéctelo por USB, acerque una tarjeta e intente de nuevo.');
    }, TIEMPO_DETECCION_MS);
    return () => clearTimeout(temporizador);
  }, [vinculando, detectado, mostrarError]);

  function cancelarVinculacion(): void {
    setVinculando(null);
    setDetectado(null);
  }

  async function confirmarVincular(uid: string): Promise<void> {
    if (!vinculando) return;
    try {
      await api.post('/rfid/vincular', { activoId: vinculando.activoId, uid });
      mostrarExito(`Tag vinculado a ${vinculando.nombre}.`);
      void cliente.invalidateQueries({ queryKey: ['activo', vinculando.activoId] });
    } catch (error) {
      mostrarError(error instanceof ErrorApi ? error.message : 'No se pudo vincular el tag.');
    } finally {
      cancelarVinculacion();
    }
  }

  async function escanear(uid: string): Promise<void> {
    try {
      const lectura = await api.post<ResultadoEscaneoRfid>('/rfid/scan', { uid });
      if (lectura.fueraDeLugar) {
        mostrarError(`${lectura.activo.nombre} debería estar en ${lectura.ubicacionEsperada}, pero se leyó en ${lectura.ubicacionLector}.`);
      } else {
        mostrarExito(`Lectura registrada: ${lectura.activo.nombre}.`);
      }
    } catch (error) {
      mostrarError(error instanceof ErrorApi ? error.message : 'No se pudo procesar la lectura.');
    }
  }

  /**
   * Paso 1 y 2: reconocer el dispositivo y conectarlo. Solo después se busca la
   * etiqueta. Un teclado escribe a ~150 ms por tecla; el lector entrega el UID en
   * milisegundos, así que el ritmo de las teclas es la firma del dispositivo.
   */
  async function reconocerYConectar(uid: string, marcas: number[]): Promise<boolean> {
    const duracionMs = marcas.length > 1 ? marcas[marcas.length - 1]! - marcas[0]! : 0;
    const intervaloMedio = marcas.length > 1 ? duracionMs / (marcas.length - 1) : Infinity;
    if (intervaloMedio > UMBRAL_RAFAGA_MS || !/^[0-9A-F]{4,32}$/i.test(uid)) {
      mostrarError('Entrada no reconocida como lector RFID. Acerque la tarjeta al lector USB.');
      return false;
    }
    try {
      await api.post('/rfid/lector/reconocido', { uid, duracionMs: Math.round(duracionMs), caracteres: uid.length });
      await api.post('/rfid/lector/conectar');
      return true;
    } catch (error) {
      mostrarError(error instanceof ErrorApi ? error.message : 'No se pudo conectar con el lector.');
      return false;
    }
  }

  function alTeclear(evento: KeyboardEvent<HTMLInputElement>): void {
    const input = evento.currentTarget;

    if (evento.key === 'Enter') {
      evento.preventDefault();
      const uid = input.value.trim();
      const marcas = marcasRef.current;
      input.value = '';
      marcasRef.current = [];
      if (!uid) return;

      void (async () => {
        if (!(await reconocerYConectar(uid, marcas))) return;
        // Paso 3: con el lector conectado se busca la etiqueta.
        if (vinculando) {
          // Se pide confirmación antes de escribir, para no vincular por accidente.
          setDetectado(uid);
        } else {
          await escanear(uid);
        }
      })();
      return;
    }

    // Algunos lectores envían los dígitos desde el teclado numérico con Shift, y
    // entonces `key` trae flechas o Inicio. El código físico sigue siendo el dígito.
    if (/^Numpad\d$/.test(evento.code)) {
      evento.preventDefault();
      input.value += evento.code.slice(-1);
      marcasRef.current.push(performance.now());
    } else if (evento.key.length === 1) {
      marcasRef.current.push(performance.now());
    }
  }

  if (!puedeLeer) return null;

  return (
    <>
      <input
        ref={catcherRef}
        id="rfid-catcher"
        aria-hidden="true"
        tabIndex={-1}
        autoComplete="off"
        inputMode="none"
        onKeyDown={alTeclear}
        style={{ position: 'fixed', top: 0, left: 0, width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
      />

      {vinculando ? (
        <div
          role="status"
          style={{
            position: 'fixed',
            left: '50%',
            top: 16,
            transform: 'translateX(-50%)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '14px 18px',
            borderRadius: 12,
            background: 'var(--acento)',
            color: '#fff',
            boxShadow: '0 8px 30px rgba(0,0,0,.35)',
            fontSize: 16,
            fontWeight: 600,
          }}
        >
          {detectado ? (
            <>
              <span>✅ Lector detectado (UID {detectado}). ¿Vincular a «{vinculando.nombre}»?</span>
              <button type="button" onClick={() => void confirmarVincular(detectado)} style={botonAviso}>
                Confirmar
              </button>
            </>
          ) : (
            <span>🔎 Verificando lector RFID… acerque una tarjeta</span>
          )}
          <button type="button" onClick={cancelarVinculacion} style={botonAviso}>
            Cancelar
          </button>
        </div>
      ) : null}
    </>
  );
}

const botonAviso = {
  background: 'rgba(255,255,255,.2)',
  color: '#fff',
  border: 0,
  borderRadius: 8,
  padding: '6px 12px',
  font: 'inherit',
  cursor: 'pointer',
} as const;
